import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Icon from "../../components/AppIcon";
import Image from "../../components/AppImage";
import Button from "../../components/ui/Button";
import MainNavigation from "../../components/ui/MainNavigation";
import FooterSection from "../homepage/components/FooterSection";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import FeaturedBrandSection from "../../components/FeaturedBrandSection";
import ComparisonModal from "../../components/ComparisonModal";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";

const WaterSoftenerCategoryPage = () => {
    const [comparisonProducts, setComparisonProducts] = useState([]);
    const [isComparisonOpen, setIsComparisonOpen] = useState(false);

    const handleAddToCompare = (product) => {
        setComparisonProducts((prev) => {
            const exists = prev?.find((p) => p?.id === product?.id);
            if (exists) {
                return prev;
            }
            if (prev?.length >= 3) {
                return [...prev?.slice(1), product];
            }
            return [...prev, product];
        });
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };

    const limescaleProblems = [
        {
            id: 1,
            title: "Slechtere werking apparaten",
            description: "Kalk hecht zich aan verwarmingselementen, waardoor apparaten minder efficiënt werken",
            icon: "AlertTriangle",
            color: "text-error",
            image: "https://images.unsplash.com/photo-1585659722983-3a675dabf23d?w=400&h=300&fit=crop",
            alt: "Close-up van kalkaanslag op verwarmingselement van wasmachine",
        },
        {
            id: 2,
            title: "Meer energieverbruik",
            description: "Een laag kalk van 1mm verhoogt het energieverbruik met 7-10%",
            icon: "TrendingUp",
            color: "text-warning",
            image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&h=300&fit=crop",
            alt: "Energiemeter toont verhoogd verbruik door kalkaanslag",
        },
        {
            id: 3,
            title: "Snellere slijtage",
            description: "Boilers, wasmachines en kranen gaan korter mee door kalkaanslag",
            icon: "Clock",
            color: "text-error",
            image: "https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=400&h=300&fit=crop",
            alt: "Defecte boiler met ernstige kalkaanslag",
        },
        {
            id: 4,
            title: "Meer schoonmaakmiddelen",
            description: "Hard water vereist meer zeep, wasmiddel en schoonmaakproducten",
            icon: "Droplet",
            color: "text-primary",
            image: "https://images.unsplash.com/photo-1563453392212-326f5e854473?w=400&h=300&fit=crop",
            alt: "Schoonmaakmiddelen en kalkaanslag op kraan",
        },
    ];

    const softenerTypes = [
        {
            id: 1,
            name: "Zoutontharder (ionenwisseling)",
            description: "Meest effectieve methode. Vervangt calcium- en magnesiumionen door natriumionen.",
            pros: ["Volledig zachte water", "Bewezen technologie", "Geschikt voor alle toepassingen"],
            cons: ["Zoutverbruik", "Periodiek onderhoud", "Afvalwater bij regeneratie"],
            icon: "Droplets",
            color: "from-blue-500/10 to-cyan-500/10",
            image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=300&fit=crop",
            alt: "Zoutontharder systeem geïnstalleerd in meterkast",
        },
        {
            id: 2,
            name: "Zoutvrije systemen",
            description: "Voorkomt kalkaanslag zonder zout, maar verwijdert kalk niet uit het water.",
            pros: ["Geen zoutverbruik", "Milieuvriendelijker", "Minder onderhoud"],
            cons: ["Minder effectief", "Water blijft \'hard'", "Niet voor alle toepassingen"],
            icon: "Leaf",
            color: "from-green-500/10 to-emerald-500/10",
            image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop",
            alt: "Zoutvrij wateronthardings systeem in technische ruimte",
        },
        {
            id: 3,
            name: "Magnetische/elektronische systemen",
            description: "Gebruikt magnetische velden of elektrische pulsen om kalkvorming te verminderen.",
            pros: ["Geen chemicaliën", "Laag onderhoud", "Eenvoudige installatie"],
            cons: ["Beperkte effectiviteit", "Wetenschappelijk omstreden", "Resultaat varieert"],
            icon: "Zap",
            color: "from-purple-500/10 to-pink-500/10",
            image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&h=300&fit=crop",
            alt: "Elektronisch waterbehandelingssysteem op waterleiding",
        },
    ];

    const sustainabilityData = [
        { aspect: "Energiebesparing", percentage: 15 },
        { aspect: "Schoonmaakmiddelen", percentage: 50 },
        { aspect: "Levensduur apparaten", percentage: 30 },
        { aspect: "Waterverbruik", percentage: -10 },
    ];

    const costData = [
        { category: "Aanschaf", zout: 800, zoutvrij: 600, magnetisch: 300 },
        { category: "Installatie", zout: 300, zoutvrij: 200, magnetisch: 100 },
        { category: "Jaarlijks onderhoud", zout: 100, zoutvrij: 50, magnetisch: 20 },
    ];

    const providers = [
        {
            id: 1,
            name: "AquaPur Waterbehandeling",
            rating: 4.8,
            reviews: 267,
            specialization: "Complete wateronthardings-oplossingen",
            region: "Landelijk",
            image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=100&h=100&fit=crop",
            alt: "AquaPur Waterbehandeling bedrijfspand met waterontharders",
        },
        {
            id: 2,
            name: "Zacht Water Installaties",
            rating: 4.7,
            reviews: 198,
            specialization: "Zoutontharders en onderhoud",
            region: "Randstad",
            image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=100&h=100&fit=crop",
            alt: "Zacht Water Installaties showroom met verschillende systemen",
        },
        {
            id: 3,
            name: "EcoWater Solutions",
            rating: 4.9,
            reviews: 334,
            specialization: "Duurzame waterbehandeling",
            region: "Landelijk",
            image: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100&h=100&fit=crop",
            alt: "EcoWater Solutions team bij waterontharder installatie",
        },
    ];

    // Define featuredBrandData
    const featuredBrandData = {
        brandName: "BWT Perla",
        brandLogo: "https://ui-avatars.com/api/?name=BWT&background=0ea5e9&color=fff&size=80",
        brandDescription: "Premium waterontharders met slimme technologie",
        productName: "BWT Perla Silk M",
        productImage: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop",
        productImageAlt:
            "BWT Perla Silk waterontharder geïnstalleerd in moderne meterkast met digitaal display en app-bediening",
        productDescription:
            "Compacte en efficiënte zoutontharder met WiFi-bediening en automatische regeneratie. Ideaal voor gezinnen tot 5 personen.",
        productSpecs: [
            { icon: "Droplets", label: "Capaciteit", value: "5 personen" },
            { icon: "Zap", label: "Verbruik", value: "3 kg zout/mnd" },
            { icon: "Wifi", label: "Smart control", value: "App-bediening" },
            { icon: "Shield", label: "Garantie", value: "10 jaar" },
        ],
        productPrice: "€1.850",
        rating: 4.7,
        reviewCount: 312,
        reviews: [
            {
                rating: 5,
                text: "Geen kalkaanslag meer! Onze wasmachine en boiler werken veel efficiënter. De app is super handig voor monitoring.",
                name: "Laura Vermeulen",
                location: "Eindhoven",
                avatar: "https://ui-avatars.com/api/?name=Laura+V&background=0ea5e9&color=fff",
                verified: true,
            },
            {
                rating: 5,
                text: "Merkbaar zachter water en veel minder schoonmaakmiddelen nodig. Installatie was snel en professioneel.",
                name: "Tom Hendriks",
                location: "Den Haag",
                avatar: "https://ui-avatars.com/api/?name=Tom+H&background=0284c7&color=fff",
                verified: true,
            },
            {
                rating: 4,
                text: "Uitstekend systeem met lage onderhoudskosten. Compact en stil. Energierekening is ook gedaald!",
                name: "Emma de Jong",
                location: "Haarlem",
                avatar: "https://ui-avatars.com/api/?name=Emma+dJ&background=0369a1&color=fff",
                verified: true,
            },
        ],
        colorScheme: "from-blue-500/10 to-cyan-500/10",
        accentColor: "text-blue-600",
        bgGradient: "from-blue-50 via-cyan-50 to-teal-50",
    };

    return (
        <div className="min-h-screen bg-background">
            <MainNavigation />
            {/* Main Content with proper spacing for fixed navigation */}
            <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
                <FeaturedBrandSection {...featuredBrandData} onAddToCompare={handleAddToCompare} />
                {/* Hero Section */}
                <motion.section
                    className="relative bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 overflow-hidden py-16 lg:py-24"
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <div className="absolute inset-0 opacity-30">
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                            <motion.div variants={itemVariants}>
                                <div className="inline-flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-full mb-6">
                                    <Icon name="Droplets" size={20} className="text-blue-600" />
                                    <span className="text-sm font-medium text-blue-800">Waterontharder</span>
                                </div>
                                <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                                    Waterontharder: minder kalk, meer comfort
                                </h1>
                                <p className="text-lg text-muted-foreground mb-6">
                                    Minder kalkaanslag, langere levensduur van apparaten en meer comfort in huis. Een
                                    waterontharder beschermt uw installaties en bespaart u geld op energie en onderhoud.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <Link to="/quote-request-form">
                                        <Button size="lg" iconName="FileText" iconPosition="left">
                                            Offerte aanvragen
                                        </Button>
                                    </Link>
                                    <Button variant="outline" size="lg" iconName="TestTube" iconPosition="left">
                                        Test waterhardheid
                                    </Button>
                                </div>
                            </motion.div>

                            <motion.div variants={itemVariants} className="relative">
                                <div className="relative rounded-2xl overflow-hidden shadow-elevation-3">
                                    <Image
                                        src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop"
                                        alt="Moderne waterontharder geïnstalleerd in meterkast naast wasmachine met voor/na kalkvergelijking"
                                        className="w-full h-auto"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-muted-foreground">Energiebesparing</p>
                                                    <p className="text-2xl font-bold text-success">Tot 15%</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm text-muted-foreground">
                                                        Levensduur apparaten
                                                    </p>
                                                    <p className="text-2xl font-bold text-primary">+30%</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.section>
                {/* What Does Limescale Do */}
                <motion.section
                    className="py-16 lg:py-20 bg-white"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div variants={itemVariants} className="text-center mb-12">
                            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                                Wat doet kalk in huis?
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                                Hard water veroorzaakt verschillende problemen in uw woning
                            </p>
                        </motion.div>

                        <div className="grid md:grid-cols-2 gap-6 mb-12">
                            {limescaleProblems?.map((problem) => (
                                <motion.div
                                    key={problem?.id}
                                    variants={itemVariants}
                                    className="bg-gradient-to-br from-gray-50 to-white rounded-xl overflow-hidden shadow-elevation-2 hover:shadow-elevation-3 transition-shadow"
                                >
                                    <div className="relative h-48 overflow-hidden">
                                        <Image
                                            src={problem?.image}
                                            alt={problem?.alt}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        <div className="absolute bottom-4 left-4">
                                            <div
                                                className={`w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center`}
                                            >
                                                <Icon name={problem?.icon} size={20} className={problem?.color} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                                            {problem?.title}
                                        </h3>
                                        <p className="text-muted-foreground">{problem?.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            variants={itemVariants}
                            className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 border border-red-100"
                        >
                            <div className="flex items-start space-x-4">
                                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <Icon name="AlertTriangle" size={24} className="text-error" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                                        Waterhardheid in Nederland
                                    </h3>
                                    <p className="text-muted-foreground mb-4">
                                        Nederland heeft regionaal zeer verschillende waterhardheid. In Limburg en delen
                                        van Noord-Brabant is het water extreem hard (&gt;20°dH), terwijl het in de
                                        Randstad zachter is (8-14°dH). Vanaf 14°dH wordt een waterontharder sterk
                                        aangeraden.
                                    </p>
                                    <Button variant="outline" size="sm" iconName="Map" iconPosition="left">
                                        Bekijk waterhardheid in uw regio
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.section>
                {/* How Water Softener Works */}
                <motion.section
                    className="py-16 lg:py-20 bg-gray-50"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div variants={itemVariants} className="text-center mb-12">
                            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                                Hoe werkt een waterontharder?
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                                Verschillende technologieën voor het verminderen van kalkaanslag
                            </p>
                        </motion.div>

                        <div className="grid lg:grid-cols-3 gap-6">
                            {softenerTypes?.map((type) => (
                                <motion.div
                                    key={type?.id}
                                    variants={itemVariants}
                                    className="bg-white rounded-xl overflow-hidden shadow-elevation-2 hover:shadow-elevation-3 transition-shadow"
                                >
                                    <div className="relative h-48 overflow-hidden">
                                        <Image
                                            src={type?.image}
                                            alt={type?.alt}
                                            className="w-full h-full object-cover"
                                        />
                                        <div
                                            className={`absolute top-4 right-4 w-12 h-12 rounded-full bg-gradient-to-br ${type?.color} backdrop-blur-sm flex items-center justify-center border border-white/20`}
                                        >
                                            <Icon name={type?.icon} size={24} className="text-foreground" />
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                                            {type?.name}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mb-4">{type?.description}</p>
                                        <div className="space-y-3 mb-4">
                                            <div>
                                                <p className="text-xs font-medium text-foreground mb-2">Voordelen:</p>
                                                {type?.pros?.map((pro, index) => (
                                                    <div key={index} className="flex items-start space-x-2 mb-1">
                                                        <Icon
                                                            name="Plus"
                                                            size={14}
                                                            className="text-success mt-0.5 flex-shrink-0"
                                                        />
                                                        <p className="text-xs text-muted-foreground">{pro}</p>
                                                    </div>
                                                ))}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-foreground mb-2">Nadelen:</p>
                                                {type?.cons?.map((con, index) => (
                                                    <div key={index} className="flex items-start space-x-2 mb-1">
                                                        <Icon
                                                            name="Minus"
                                                            size={14}
                                                            className="text-error mt-0.5 flex-shrink-0"
                                                        />
                                                        <p className="text-xs text-muted-foreground">{con}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.section>
                {/* Sustainability */}
                <motion.section
                    className="py-16 lg:py-20 bg-white"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div variants={itemVariants} className="text-center mb-12">
                            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                                Is een waterontharder duurzaam?
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                                De milieu-impact van waterontharders in perspectief
                            </p>
                        </motion.div>

                        <div className="grid lg:grid-cols-2 gap-8">
                            <motion.div variants={itemVariants}>
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 lg:p-8 border border-green-100 h-full">
                                    <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                                        Duurzaamheidsvoordelen
                                    </h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={sustainabilityData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis
                                                dataKey="aspect"
                                                tick={{ fontSize: 11 }}
                                                angle={-15}
                                                textAnchor="end"
                                                height={80}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "white",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: "8px",
                                                    fontSize: "14px",
                                                }}
                                                formatter={(value) => `${value > 0 ? "+" : ""}${value}%`}
                                            />
                                            <Bar dataKey="percentage" fill="#10b981" radius={[8, 8, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 lg:p-8 border border-blue-100 h-full">
                                    <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                                        Milieu-overwegingen
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                <Icon name="TrendingDown" size={20} className="text-success" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-foreground mb-1">Energiebesparing</h4>
                                                <p className="text-sm text-muted-foreground">
                                                    Minder kalkaanslag betekent efficiëntere apparaten en tot 15% lagere
                                                    energiekosten
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                <Icon name="Droplet" size={20} className="text-success" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-foreground mb-1">
                                                    Minder schoonmaakmiddelen
                                                </h4>
                                                <p className="text-sm text-muted-foreground">
                                                    Tot 50% minder zeep, wasmiddel en schoonmaakproducten nodig
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                <Icon name="Clock" size={20} className="text-success" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-foreground mb-1">
                                                    Langere levensduur installaties
                                                </h4>
                                                <p className="text-sm text-muted-foreground">
                                                    Boilers, wasmachines en andere apparaten gaan 30% langer mee
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start space-x-3">
                                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                                <Icon name="AlertCircle" size={20} className="text-warning" />
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-foreground mb-1">
                                                    Zoutverbruik & afvalwater
                                                </h4>
                                                <p className="text-sm text-muted-foreground">
                                                    Zoutontharders verbruiken zout en produceren afvalwater bij
                                                    regeneratie
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.section>
                {/* Costs & Considerations */}
                <motion.section
                    className="py-16 lg:py-20 bg-gray-50"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div variants={itemVariants} className="text-center mb-12">
                            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                                Kosten & aandachtspunten
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                                Investering en onderhoudskosten per type waterontharder
                            </p>
                        </motion.div>

                        <div className="grid lg:grid-cols-2 gap-8">
                            <motion.div variants={itemVariants}>
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-elevation-2 h-full">
                                    <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                                        Kostenvergelijking
                                    </h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={costData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: "white",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: "8px",
                                                    fontSize: "14px",
                                                }}
                                                formatter={(value) => `€${value}`}
                                            />
                                            <Bar
                                                dataKey="zout"
                                                fill="#3b82f6"
                                                radius={[8, 8, 0, 0]}
                                                name="Zoutontharder"
                                            />
                                            <Bar
                                                dataKey="zoutvrij"
                                                fill="#10b981"
                                                radius={[8, 8, 0, 0]}
                                                name="Zoutvrij"
                                            />
                                            <Bar
                                                dataKey="magnetisch"
                                                fill="#8b5cf6"
                                                radius={[8, 8, 0, 0]}
                                                name="Magnetisch"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </motion.div>

                            <motion.div variants={itemVariants}>
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-elevation-2 h-full">
                                    <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                                        Belangrijke aandachtspunten
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                                            <div className="flex items-start space-x-3">
                                                <Icon
                                                    name="TestTube"
                                                    size={20}
                                                    className="text-primary mt-0.5 flex-shrink-0"
                                                />
                                                <div>
                                                    <h4 className="font-medium text-foreground mb-1">
                                                        Waterkwaliteit testen
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        Laat uw waterhardheid meten voordat u investeert in een systeem
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                                            <div className="flex items-start space-x-3">
                                                <Icon
                                                    name="Wrench"
                                                    size={20}
                                                    className="text-success mt-0.5 flex-shrink-0"
                                                />
                                                <div>
                                                    <h4 className="font-medium text-foreground mb-1">
                                                        Professionele installatie
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        Laat het systeem installeren door een erkend installateur voor
                                                        optimale werking
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                                            <div className="flex items-start space-x-3">
                                                <Icon
                                                    name="Calendar"
                                                    size={20}
                                                    className="text-purple-600 mt-0.5 flex-shrink-0"
                                                />
                                                <div>
                                                    <h4 className="font-medium text-foreground mb-1">
                                                        Regelmatig onderhoud
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        Zout bijvullen en jaarlijkse controle zorgen voor langdurige
                                                        werking
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                                            <div className="flex items-start space-x-3">
                                                <Icon
                                                    name="Home"
                                                    size={20}
                                                    className="text-warning mt-0.5 flex-shrink-0"
                                                />
                                                <div>
                                                    <h4 className="font-medium text-foreground mb-1">
                                                        Ruimte & aansluiting
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground">
                                                        Zorg voor voldoende ruimte in meterkast en afvoermogelijkheid
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.section>
                {/* Providers & Reviews */}
                <motion.section
                    className="py-16 lg:py-20 bg-white"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={containerVariants}
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div variants={itemVariants} className="text-center mb-12">
                            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
                                Aanbieders & ervaringen
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                                Gecertificeerde installateurs met bewezen expertise in waterbehandeling
                            </p>
                        </motion.div>

                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            {providers?.map((provider) => (
                                <motion.div
                                    key={provider?.id}
                                    variants={itemVariants}
                                    className="bg-white rounded-xl p-6 shadow-elevation-2 hover:shadow-elevation-3 transition-shadow border border-gray-100"
                                >
                                    <div className="flex items-start space-x-4 mb-4">
                                        <Image
                                            src={provider?.image}
                                            alt={provider?.alt}
                                            className="w-16 h-16 rounded-lg object-cover"
                                        />
                                        <div className="flex-1">
                                            <h3 className="font-heading font-semibold text-foreground mb-1">
                                                {provider?.name}
                                            </h3>
                                            <div className="flex items-center space-x-1 mb-1">
                                                <Icon name="Star" size={16} className="text-warning fill-current" />
                                                <span className="text-sm font-medium text-foreground">
                                                    {provider?.rating}
                                                </span>
                                                <span className="text-sm text-muted-foreground">
                                                    ({provider?.reviews} reviews)
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-2">
                                        <span className="font-medium text-foreground">Specialisatie:</span>{" "}
                                        {provider?.specialization}
                                    </p>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        <span className="font-medium text-foreground">Regio:</span> {provider?.region}
                                    </p>
                                    <Link to="/leveranciers">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full"
                                            iconName="ArrowRight"
                                            iconPosition="right"
                                        >
                                            Bekijk profiel
                                        </Button>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div variants={itemVariants} className="text-center">
                            <Link to="/quote-request-form">
                                <Button size="lg" iconName="FileText" iconPosition="left">
                                    Offerte aanvragen bij meerdere aanbieders
                                </Button>
                            </Link>
                        </motion.div>
                    </div>
                </motion.section>
                <FooterSection />
            </main>
            {/* Floating Compare Button */}
            <AnimatePresence>
                {comparisonProducts?.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                        className="fixed bottom-6 right-6 z-40"
                    >
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setIsComparisonOpen(true)}
                            className="bg-primary text-white px-6 py-4 rounded-full shadow-2xl flex items-center space-x-3 hover:bg-primary/90 transition-colors"
                        >
                            <Icon name="GitCompare" size={24} />
                            <span className="font-semibold">Vergelijk ({comparisonProducts?.length})</span>
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* Comparison Modal */}
            <ComparisonModal
                isOpen={isComparisonOpen}
                onClose={() => setIsComparisonOpen(false)}
                products={comparisonProducts}
                category="waterontharder"
            />
        </div>
    );
};

export default WaterSoftenerCategoryPage;
